import * as React from "react"

import {
  MOCK_NOTIFICATIONS,
  type NotificationItem,
} from "@/lib/mock/notifications"

let items: NotificationItem[] = MOCK_NOTIFICATIONS
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

export function getNotifications(): NotificationItem[] {
  return items
}

export function subscribeNotifications(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

function samePayload(a: NotificationItem, b: NotificationItem): boolean {
  return (
    a.title === b.title &&
    a.description === b.description &&
    a.variant === b.variant &&
    a.href === b.href &&
    a.kind === b.kind &&
    a.timestamp === b.timestamp
  )
}

/** Insert or replace by id, newest first. Returns false when the payload is unchanged. */
export function upsertNotification(item: NotificationItem): boolean {
  const existing = items.find((entry) => entry.id === item.id)
  const next: NotificationItem =
    existing && samePayload(existing, item)
      ? existing
      : {
          ...item,
          read: false,
          seen: false,
        }

  if (existing && next === existing) return false

  items = [next, ...items.filter((entry) => entry.id !== item.id)]
  emit()
  return true
}

export function removeNotification(id: string) {
  if (!items.some((entry) => entry.id === id)) return
  items = items.filter((entry) => entry.id !== id)
  emit()
}

export function markNotificationRead(id: string) {
  let changed = false
  items = items.map((entry) => {
    if (entry.id !== id || (entry.read && entry.seen)) return entry
    changed = true
    return { ...entry, read: true, seen: true }
  })
  if (changed) emit()
}

export function markAllNotificationsRead() {
  if (!items.some((entry) => !entry.read || !entry.seen)) return
  items = items.map((entry) =>
    entry.read && entry.seen ? entry : { ...entry, read: true, seen: true }
  )
  emit()
}

/** Opening the sheet clears the badge without marking rows read. */
export function markNotificationsSeen() {
  if (!items.some((entry) => !entry.seen)) return
  items = items.map((entry) => (entry.seen ? entry : { ...entry, seen: true }))
  emit()
}

export function resetNotificationsForTests(
  next: NotificationItem[] = MOCK_NOTIFICATIONS
) {
  items = next
  emit()
}

export function useNotifications(): NotificationItem[] {
  return React.useSyncExternalStore(
    subscribeNotifications,
    getNotifications,
    getNotifications
  )
}
